import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Checkbox,
  ControlLabel,
  Dropdown,
  DropdownItem,
  Grid,
  Icon,
  Popover,
  Spinner,
  TextInput,
  Tooltip,
  Typography,
} from 'gu-ui-kit'
import PropTypes from 'prop-types'
import STATISTICS_API from 'api/statistics/v2'
import ActionButtons from 'components/Filters/ActionButtons'
import styles from 'modules/Statistics/statistics.module.scss'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'
import { notificationShow } from 'modules/Notifications/actions'
import { useDispatch } from 'react-redux'
import i18n from 'i18next'

const AccountManager = ({
  groups = [],
  account = {},
  onApply = () => {},
  onReset = () => {},
  ButtonComponent = () => (
    <Icon
      name="document-edit"
      size="m"
      color={'Interface-Seventh'}
    />
  ),
  isDemoMode = false,
}) => {
  const dispatch = useDispatch()
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const { t } = useTranslation()

  const popoverState = usePopupState({
    variant: 'popover',
    id: 'accountManagerPopover' + account.id,
    breakpoints: {
      0: {
        variant: 'bottomSheet',
      },
      600: {
        variant: 'popover',
      },
    },
  })

  const [isCreateMode, setIsCreateMode] = useState(false)
  const [draftGroupName, setDraftGroupName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [localGroups, setLocalGroups] = useState(Array.isArray(groups) ? groups : [])

  useEffect(() => {
    setLocalGroups(Array.isArray(groups) ? groups : [])
  }, [groups])

  const selectedIds = account?.groups.map((g) => String(g?.id)) ?? []
  const [draftSelectedGroupIds, setDraftSelectedGroupIds] = useState(selectedIds)

  const initialSortDoneRef = useRef(false)

  useEffect(() => {
    if (initialSortDoneRef.current) return
    if (!draftSelectedGroupIds.length && !localGroups.length) return
    initialSortDoneRef.current = true

    const selected = new window.Set(draftSelectedGroupIds)
    setLocalGroups((prev) =>
      [...prev].sort((a, b) => {
        const aSelected = selected.has(String(a?.id)) ? 0 : 1
        const bSelected = selected.has(String(b?.id)) ? 0 : 1
        return aSelected - bSelected
      }),
    )
  }, [draftSelectedGroupIds, localGroups])

  const toggleGroup = useCallback((groupId) => {
    const gid = String(groupId)
    setDraftSelectedGroupIds((prev) =>
      prev.includes(gid) ? prev.filter((id) => id !== gid) : prev.concat(gid),
    )
  }, [])

  const dropSelection = useCallback(() => {
    setDraftSelectedGroupIds(selectedIds)
    setLocalGroups(Array.isArray(groups) ? groups : [])
    onReset()
  }, [groups, onReset, selectedIds])

  const applyValue = useCallback(() => {
    if (!account?.id && account?.id !== 0) return

    onApply({ account, groupIds: draftSelectedGroupIds })
    popoverState.close()
  }, [account, draftSelectedGroupIds, onApply, popoverState])

  const createGroup = useCallback(async () => {
    const name = (draftGroupName ?? '').trim()
    if (!name || isCreating) return

    setIsCreating(true)
    try {
      const { response, error } = await STATISTICS_API.createGroup({
        data: { name, account_ids: account?.id ? [account.id] : [] },
      })

      if (error) throw error

      setLocalGroups((current) => [response.data].concat([...current]))
      toggleGroup(response.data.id)

      setDraftGroupName('')
      setIsCreateMode(false)
      dispatch(
        notificationShow({
          type: 'success',
          message: i18n.t('statistics::table.accounts_manager.group_creation', {
            context: 'success',
          }),
        }),
      )
    } catch (e) {
      dispatch(
        notificationShow({
          type: 'error',
          message: i18n.t('statistics::table.accounts_manager.group_creation', {
            context: 'failed',
          }),
        }),
      )
    } finally {
      setIsCreating(false)
    }
  }, [draftGroupName, isCreating])

  return isDemoMode ? (
    <Tooltip title={t('statistics::tooltips.not_available_for_demo')}>
      <ButtonComponent />
    </Tooltip>
  ) : (
    <>
      <div {...bindTrigger(popoverState)}>
        <ButtonComponent />
      </div>
      <Popover
        {...bindPopover(popoverState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 8,
          horizontal: 'left',
        }}
      >
        <Dropdown className={styles['group-manager__dropdown']}>
          <DropdownItem noHover>
            {isCreateMode ? (
              <TextInput
                disabled={isCreating}
                className={styles['group-manager__input']}
                value={draftGroupName}
                onChange={(e) => setDraftGroupName(() => e.target.value)}
                fullWidth
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    e.preventDefault()
                    createGroup()
                    return
                  }

                  return true
                }}
                variant={'outlined'}
                label={t('statistics::table.groups_manager.group_name_placeholder')}
                InputProps={{
                  endAdornment:
                    (draftGroupName ?? '').trim().length > 0 ? (
                      isCreating ? (
                        <Spinner width={24} />
                      ) : (
                        <Button
                          noLabel
                          size={'m'}
                          noHover
                          color={'Interface-Seventh'}
                          startIcon={
                            <Icon
                              name={'check'}
                              size={'m'}
                            />
                          }
                          disabled={isCreating}
                          onClick={createGroup}
                        />
                      )
                    ) : null,
                }}
              />
            ) : (
              <Button
                className={styles['group-manager__create-group-button']}
                size={'m'}
                color={'Label-GU-Primary'}
                variant={'link'}
                startIcon={<Icon name={'plus'} />}
                onClick={() => setIsCreateMode(true)}
              >
                <Typography
                  variant={'Body-Forms-20'}
                  component={'div'}
                >
                  {t('statistics::table.accounts_manager.group_name_placeholder')}
                </Typography>
              </Button>
            )}
          </DropdownItem>

          <div className={styles['group-manager__list']}>
            {(Array.isArray(localGroups) ? localGroups : []).map((group) => {
              return (
                <DropdownItem
                  key={`account-manager-group-${group?.id}`}
                  noHover
                >
                  <ControlLabel
                    onChange={() => toggleGroup(group?.id)}
                    checked={draftSelectedGroupIds.includes(String(group?.id))}
                    value={group?.id}
                    name={'accountGroups'}
                    control={<Checkbox />}
                    className={styles['group-manager__control-label']}
                    label={
                      <Grid
                        wrap={'nowrap'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        gap={8}
                        className={styles['group-manager__accounts-row']}
                      >
                        <Typography
                          variant={'Body-Forms-15'}
                          component={'div'}
                          className={styles['group-manager__account-label']}
                        >
                          {group?.name ?? '—'}
                        </Typography>
                      </Grid>
                    }
                  />
                </DropdownItem>
              )
            })}
          </div>

          <ActionButtons
            disabled={isEqual(
              account?.groups.map((g) => String(g?.id)),
              draftSelectedGroupIds,
            )}
            showActionButtons
            dropFilter={dropSelection}
            applyValue={applyValue}
          />
        </Dropdown>
      </Popover>
    </>
  )
}

export default AccountManager

AccountManager.propTypes = {
  ButtonComponent: PropTypes.elementType,
  groups: PropTypes.array,
  account: PropTypes.object,
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  isDemoMode: PropTypes.bool,
}
